/**
 * The Wall-E list, read for a person (DEC-28).
 *
 * Pure, so the page and its tests agree on one wording. The tokens on the list
 * are shown to signed-in readers, as the decisions are; nothing here sends one
 * anywhere.
 */

import type { WallEListEntry, WallEListKind } from '../api'

export const KIND_LABELS: Record<WallEListKind, string> = {
  card: 'Card',
  label: 'Routing label',
  placeholder: 'Placeholder',
  kiosk: 'Kiosk tag',
  vehicle: 'Vehicle id',
  test: 'Test token',
  other: 'Other',
}

const VIA_LABELS: Record<string, string> = {
  console: 'EAS console',
  ocpp_admin: 'OCPP Admin',
  import: 'import',
}

export function viaLabel(via: string): string {
  return VIA_LABELS[via] ?? via
}

// WHAT STANDS IN FOR A CARD, as the service reads it when it looks for the
// owner of a session (backend/src/logic/unowned.py, `is_placeholder`).
// `test_the_console_names_stand_ins_as_the_service_does.py` holds the two
// together: a second statement of a rule drifts unless something compares it.
const NOT_A_CARD = ['', 'zero', 'zeor', '0', 'remote', 'predefinedtokenid']
const KIOSK_TAG = /^hyckiosktag\d*$/i
const ROUTING_LABEL = /^DCS_[A-Z_]+$/

export function standsInForACard(token: string): boolean {
  const value = token.trim()
  return NOT_A_CARD.includes(value.toLowerCase()) || KIOSK_TAG.test(value) || ROUTING_LABEL.test(value)
}

/**
 * Who pays for a session the entry approved, in words.
 *
 * A list approval names no payer (DEC-28): the session goes where it would
 * have gone had nobody approved it, and that is one of DEC-25's two rules. A
 * token that stands in for a card belongs to the approval at the station just
 * before, whatever card it was; any other token only to an approval of that
 * same token.
 */
export function whoPays(entry: Pick<WallEListEntry, 'status' | 'token'>): string {
  if (entry.status !== 'accepted') return 'Nobody: refused'
  return entry.token !== null && standsInForACard(entry.token)
    ? 'Any approval here in the 180 s before, or nobody'
    : 'An approval of this token in the 15 min before, or nobody'
}

export interface ListTotals {
  total: number
  accepted: number
  blocked: number
  byKind: Record<WallEListKind, number>
  usedIn30Days: number
  neverUsed: number
}

const DAY_MS = 86_400_000

export function totals(entries: WallEListEntry[], now: number): ListTotals {
  const byKind = Object.fromEntries(
    Object.keys(KIND_LABELS).map((kind) => [kind, 0]),
  ) as Record<WallEListKind, number>
  let accepted = 0
  let usedIn30Days = 0
  let neverUsed = 0
  for (const entry of entries) {
    byKind[entry.kind] = (byKind[entry.kind] ?? 0) + 1
    if (entry.status === 'accepted') accepted += 1
    if (!entry.last_used_at) neverUsed += 1
    else if (now - Date.parse(entry.last_used_at) <= 30 * DAY_MS) usedIn30Days += 1
  }
  return {
    total: entries.length,
    accepted,
    blocked: entries.length - accepted,
    byKind,
    usedIn30Days,
    neverUsed,
  }
}

/** Whether an entry answers a search, in any case and ignoring the space a
 *  person types around a token. A part of a token is enough. */
export function matches(entry: WallEListEntry, query: string): boolean {
  const wanted = query.trim().toUpperCase()
  if (!wanted) return true
  return (entry.token ?? '').toUpperCase().includes(wanted)
}
